import { useNavigate } from 'react-router-dom';
import Drawer from '@/components/base/Drawer';
import Badge, { statusTone } from '@/components/base/Badge';
import { useAppStore } from '@/store/AppStore';
import { formatTL } from '@/lib/format';
import { QUOTE_STATUSES } from '@/mocks/constants';
import type { Quote, QuoteStatus } from '@/types';

export default function QuotePreview({ quote, onClose }: { quote: Quote; onClose: () => void }) {
  const { setQuoteStatus, customers } = useAppStore();
  const navigate = useNavigate();

  const customer = customers.find((c) => c.name === quote.customerName);
  const approved = quote.status === 'Onaylandı';

  function handleCreateAppointment() {
    navigate('/randevu', {
      state: {
        prefillCustomer: quote.customerName,
        prefillService: quote.service,
        prefillQuoteId: quote.id,
        prefillQuoteAmount: quote.amount,
        prefillLocation: quote.location,
      },
    });
  }

  return (
    <Drawer open onClose={onClose} title={quote.id} width="max-w-2xl">
      <div className="space-y-6">
        {/* Premium preview */}
        <div className="rounded-xl border border-background-200 bg-white p-6">
          <div className="flex items-center justify-between border-b-2 border-primary-950 pb-3">
            <div>
              <div className="font-heading text-lg font-extrabold tracking-tight text-primary-950">VİAYPİİ</div>
              <div className="text-[11px] font-semibold tracking-[0.2em] text-accent-600">TEMİZLİK</div>
            </div>
            <div className="text-right text-xs text-foreground-500">
              <div>Teklif No</div>
              <div className="font-semibold text-foreground-800">{quote.id}</div>
              {quote.revision > 1 && <div className="mt-0.5 text-accent-600">Revizyon {quote.revision}</div>}
            </div>
          </div>
          <div className="mt-4 space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-foreground-500">Müşteri</span>
              <span className="font-semibold text-foreground-900">{quote.customerName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-foreground-500">Hizmet</span>
              <span className="font-semibold text-foreground-900">{quote.service}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-foreground-500">Konum</span>
              <span className="font-semibold text-foreground-900">{quote.location}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-foreground-500">Tarih</span>
              <span className="font-semibold text-foreground-900">{quote.date}</span>
            </div>
          </div>
          {quote.scope && (
            <p className="mt-3 rounded-md bg-background-50 p-3 text-xs text-foreground-600">{quote.scope}</p>
          )}
          <div className="mt-4 flex items-center justify-between rounded-lg bg-primary-950 px-4 py-3">
            <span className="text-sm font-semibold text-primary-200">Toplam</span>
            <span className="font-heading text-lg font-bold text-accent-400">{formatTL(quote.amount)}</span>
          </div>
        </div>

        {/* Status */}
        <div className="flex items-center gap-3">
          <Badge tone={statusTone(quote.status)}>{quote.status}</Badge>
          <select
            className="h-10 flex-1 rounded-md border border-background-200 bg-white px-3 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
            value={quote.status}
            onChange={(e) => setQuoteStatus(quote.id, e.target.value as QuoteStatus)}
          >
            {QUOTE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        {/* Notes */}
        {(quote.customerNote || quote.operationalNote) && (
          <div className="space-y-2">
            {quote.customerNote && (
              <p className="rounded-lg bg-background-50 p-3 text-sm text-foreground-600">
                <span className="font-semibold text-foreground-800">Müşteri notu: </span>
                {quote.customerNote}
              </p>
            )}
            {quote.operationalNote && (
              <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
                <span className="font-semibold">Dahili not: </span>
                {quote.operationalNote}
              </p>
            )}
          </div>
        )}

        {/* Contact */}
        {customer && (
          <div className="flex flex-wrap gap-2">
            <a
              href={`tel:${customer.phone.replace(/\s/g, '')}`}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 px-3 py-2 text-sm font-semibold text-foreground-700 hover:bg-background-100"
            >
              <i className="ri-phone-line" /> Ara
            </a>
            <a
              href={`https://wa.me/90${customer.phone.replace(/\s/g, '').slice(1)}`}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 px-3 py-2 text-sm font-semibold text-foreground-700 hover:bg-background-100"
            >
              <i className="ri-whatsapp-line" /> WhatsApp'ta Paylaş
            </a>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 px-3 py-2 text-sm font-semibold text-foreground-700 hover:bg-background-100"
              onClick={() => window.print()}
            >
              <i className="ri-file-pdf-line" /> PDF Önizleme
            </button>
          </div>
        )}

        {/* Approved → create appointment */}
        {approved && (
          <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="font-heading text-sm font-bold text-emerald-900">Teklif Onaylandı</div>
                <p className="text-xs text-emerald-700">Müşteri onayladı — randevu oluşturabilirsiniz.</p>
              </div>
              <button
                type="button"
                onClick={handleCreateAppointment}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-600"
              >
                <i className="ri-calendar-line" />
                Randevu Oluştur
              </button>
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
}